import type { Task, Timesheet, KPIDef, PlanItem } from '@/types'
import { inPeriod, inDateRange, type FilterParams } from './filters'
import { isoWeek } from './dates'

export const SLA_THRESHOLDS: Record<string, number> = { MSO: 10, PSO: 20, Internal: 30 }

// Placeholder cost rate until real per-engineer rates exist — a flat €33/h applied to
// every logged hour, regardless of who logged it.
export const FLAT_HOURLY_COST_EUR = 33

// Monthly revenue per project (July 2026), EUR-converted — keyed by the exact Odoo
// project name so contracts under the same client stay separate (e.g. Aleph has three:
// IM/OM, FTE, DevOps FTE — collapsing them by client would blend three different
// contracts' margins into one meaningless number). Projects not listed here show
// "pending" instead of a guessed margin.
export const REVENUE_BY_PROJECT: Record<string, number> = {
  '[AWS] IM/OM - Vertical Fox LLC - 2025 - [02]': 3900.60,
  '[AWS] IM/OM - Kistler - 2025 - [02]': 4898.15,
  '[GCP] Heureka - IM Light - 2025 [02]': 2000.00,
  '[AWS] Incident Management - Eyeball - 2025 - [02]': 0,
  '[GCP] Sufio - Incident Management - 2025 - [02]': 2500.00,
  '[GCP] PagerDuty Usage - Sufio - 2025 - [02]': 255.31,
  '[GCP] IM/OM - GoOut - 2025 - [02]': 8000.00,
  '[AWS] IM/OM - Otherside Entertainment - 2025 [02]': 0,
  '[GCP] DevOps Support - OnSinch - 2025 [02]': 0,
  '[GCP] Incident Management - Popron - 2025 [02]': 1150.00,
  'GotPhoto [02]': 1300.00,
  'Aleph IM/OM': 8800.00,
  'Aleph FTE': 12000.00,
  'Aleph DevOps FTE': 23800.00,
  'Eesti [02]': 0,
  'Innogy Cognito consultations': 0,
  'Innogy API gateway': 0,
  'Innogy SES Migration': 831.44,
  'Livesport': 4698.00,
  'Microblink': 2405.90,
  'Auvious': 0,
}

// The revenue sheet's project names don't always match Odoo's live project names
// (renamed/reworded on one side or the other) — this maps the real Odoo name to the
// label used in REVENUE_BY_PROJECT. Two entries deliberately point at the same label
// (the two active "Eesti" projects) so their hours combine into one revenue line,
// since the revenue sheet doesn't split them. Verified against real activity: e.g.
// "[MSO][AWS] IM - Eyeball" has zero tasks/hours (a dead duplicate) and is
// intentionally left unmapped — "[MSO][AWS] Eyeball - IM" is the active one.
export const PROJECT_ALIASES: Record<string, string> = {
  '[MSO][AWS] IM/OM - VerticalFox': '[AWS] IM/OM - Vertical Fox LLC - 2025 - [02]',
  '[MSO][AWS] IM/OM - Kistler': '[AWS] IM/OM - Kistler - 2025 - [02]',
  '[MSO][GCP] IM Light - Heureka': '[GCP] Heureka - IM Light - 2025 [02]',
  '[MSO][AWS] Eyeball - IM': '[AWS] Incident Management - Eyeball - 2025 - [02]',
  '[MSO][GCP] IM - Sufio': '[GCP] Sufio - Incident Management - 2025 - [02]',
  '[MSO][AWS] IM/OM - GoOut': '[GCP] IM/OM - GoOut - 2025 - [02]',
  '[MSO][AWS] IM/OM - Otherside': '[AWS] IM/OM - Otherside Entertainment - 2025 [02]',
  '[MSO][AWS] DevOps Support - OnSinch': '[GCP] DevOps Support - OnSinch - 2025 [02]',
  '[MSO][GCP] IM - Popron Systems': '[GCP] Incident Management - Popron - 2025 [02]',
  '[MSO][AWS] GotPhoto WIZ Platform Mngmnt ': 'GotPhoto [02]',
  'Aleph Holding IM/OM': 'Aleph IM/OM',
  '[AWS][PSO] Aleph FTE': 'Aleph FTE',
  '[AWS] Aleph - DevOps FTE': 'Aleph DevOps FTE',
  '[PSO][AWS] Innogy - Cognito consultations': 'Innogy Cognito consultations',
  '[PSO][AWS] Innogy IaC Automation - API Gateway': 'Innogy API gateway',
  '[AWS][PSO] Innogy - SES migration': 'Innogy SES Migration',
  'Eesti- Presales': 'Eesti [02]',
  '[PSO][AWS] Eesti Interneti': 'Eesti [02]',
  '[PSO][AWS] Eesti Interneti Consultation': 'Eesti [02]',
}

export interface AccountMargin {
  client: string
  hours: number
  cost: number
  revenue: number | null
  margin: number | null
  marginPct: number | null
}

// Grouped by project name (aliased to the revenue sheet's label where one exists),
// matching the granularity revenue is actually tracked at — one row per contract, not
// per client. `timesheets` should already be scoped to the period being shown; cost
// is a flat-rate proxy (hours × FLAT_HOURLY_COST_EUR) since real per-engineer rates
// aren't available yet. Margin is the standard definition: (revenue − cost) / revenue
// × 100 — a percentage, not a raw € amount. Null (not 0% or Infinity) when revenue is
// 0 or unknown, since a margin percentage is meaningless without real revenue to
// divide by.
export function computeAccountMargins(timesheets: Timesheet[]): AccountMargin[] {
  const hoursByProj: Record<string, number> = {}
  timesheets.forEach(ts => {
    const key = PROJECT_ALIASES[ts.proj] ?? ts.proj
    hoursByProj[key] = (hoursByProj[key] || 0) + ts.hours
  })

  return Object.entries(hoursByProj).map(([proj, hours]) => {
    const cost = hours * FLAT_HOURLY_COST_EUR
    const revenue = REVENUE_BY_PROJECT[proj] ?? null
    const margin = revenue !== null ? revenue - cost : null
    const marginPct = revenue !== null && revenue > 0 ? (margin! / revenue) * 100 : null
    return { client: proj, hours, cost, revenue, margin, marginPct }
  }).sort((a, b) => b.hours - a.hours)
}

// For Internal-category tasks, `t.client` is often populated with Revolgy's own
// legal entity rather than being empty — grouping by the raw field then reports
// your own company name as if it were a client. Normalize to a plain "Internal"
// bucket instead, so drill-downs only name real external clients.
export function clientLabel(t: Task): string {
  return t.cat === 'Internal' ? 'Internal' : (t.client || 'Internal')
}

// Groups tasks by an arbitrary key (client, engineer, ...) and returns the top N by
// count — used to name the likely driver(s) behind an aggregate metric moving, e.g.
// "mostly Kistler and Popron" instead of just "WIP is up."
export function topTaskGroups(tasks: Task[], keyFn: (t: Task) => string, n = 2): Array<{ key: string; count: number }> {
  const counts: Record<string, number> = {}
  tasks.forEach(t => {
    const k = keyFn(t)
    if (!k) return
    counts[k] = (counts[k] || 0) + 1
  })
  return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, n).map(([key, count]) => ({ key, count }))
}

// Recurring maintenance work, not real delivery work — excluded from SLA, capacity,
// and insight views everywhere in the app (shared so both pages agree on this).
export const isRecurring = (t: Task): boolean => !!t.tags?.includes('AWS Recurring')

export function computeSlaStatus(t: Task): { sla: number; slaPct: number } {
  const sla = SLA_THRESHOLDS[t.cat] || 30
  return { sla, slaPct: (t.age || 0) / sla * 100 }
}

export interface OverdueTask {
  task: Task
  daysOverdue: number
}

// Deadline-passed only. "SLA breach" (age vs a category threshold, via
// computeSlaStatus) is a different, deadline-independent idea — a task can breach
// it with no deadline set at all, which is exactly what "Deadlines" should not
// include. The Delivery page's own SLA table still uses computeSlaStatus directly.
// `activeTasks` should already exclude Done/Backlog/cancelled (useFilteredData().activeTasks).
export function computeOverdueTasks(activeTasks: Task[]): OverdueTask[] {
  const today = new Date().toISOString().slice(0, 10)
  return activeTasks
    .filter(t => t.deadline && t.deadline < today)
    .map(t => ({ task: t, daysOverdue: Math.round((Date.now() - new Date(t.deadline).getTime()) / 86400000) }))
    .sort((a, b) => b.daysOverdue - a.daysOverdue)
}

// Tasks that have been open a long time regardless of stage/status — a coarser
// signal than SLA (fixed per-category threshold) or "stuck" (time in current
// status only). `activeTasks` should already exclude Done/Backlog/cancelled.
export function computeLongRunningTasks(activeTasks: Task[], thresholdDays = 90): Task[] {
  return activeTasks
    .filter(t => (t.age || 0) > thresholdDays)
    .sort((a, b) => (b.age || 0) - (a.age || 0))
}

// `previous === 0` can't produce a ratio: null means "no prior baseline" (still
// notable if current isn't also 0), rather than a misleading 0%/Infinity.
export function pctDelta(current: number, previous: number): number | null {
  if (previous === 0) return current === 0 ? 0 : null
  return ((current - previous) / previous) * 100
}

export function isNotable(pctChange: number | null, thresholdPct: number): boolean {
  return pctChange === null || Math.abs(pctChange) >= thresholdPct
}

// Reconstructs how many tasks were open "as of" a given moment, purely from task
// fields — used to compare "WIP now" against "WIP `period` days ago" as two
// point-in-time counts.
export function computeOpenCount(tasks: Task[], asOf: Date): number {
  return tasks.filter(t => {
    const created = new Date(t.created)
    const closed = t.stage === 'Done' && t.last_stage ? new Date(t.last_stage) : null
    return created <= asOf && (!closed || closed > asOf) && t.stage !== 'Backlog'
  }).length
}

// % of done tasks (that had a deadline at all) which finished on or before it.
export function computeDeadlineAdherence(doneTasks: Task[]): number | null {
  const withDeadline = doneTasks.filter(t => t.deadline)
  if (withDeadline.length === 0) return null
  const onTime = withDeadline.filter(t => t.last_stage <= t.deadline).length
  return (onTime / withDeadline.length) * 100
}

export const WEEKLY_CAPACITY_HOURS = 30
export const WORKDAYS_PER_WEEK = 5
export const HOURS_PER_WORKDAY = WEEKLY_CAPACITY_HOURS / WORKDAYS_PER_WEEK

// Workdays remaining in the current Mon-Fri week, counting today. Weekends return 0 —
// there's nothing left in "this work week" once it's over.
export function remainingWorkdaysThisWeek(today = new Date()): number {
  const day = today.getDay() // 0 Sun .. 6 Sat
  if (day === 0 || day === 6) return 0
  return 6 - day
}

export interface EngineerCapacity {
  name: string
  tasks: Array<Task & { stageKind: 'inProgress' | 'backlog'; leftInEstimation: number | null; toggledThisWeek: number }>
  totalLeftInEstimation: number
  totalToggledThisWeek: number
  remainingCapacity: number
  freeHours: number
}

// Free capacity = hours left in the work week (remaining workdays × 6h/day), minus,
// per assigned task (in progress + backlog):
//   - "Left in Estimation" = alloc_h − eff_h, floored at 0 — the remaining estimate on
//     that task specifically, not its full original estimate
//   - "Toggled This Week" = hours logged against THAT task this week
// Scoping "toggled" to each listed task (rather than an engineer-wide logged-hours
// total) avoids double-counting: hours already logged against a task are already
// reflected in its lower "left in estimation," so a global total would subtract the
// same effort twice.
// A task with no estimate (alloc_h = 0) has unknown remaining hours (null), not 0 —
// 0 would wrongly read as "nothing left" when really there's just no estimate.
// `assignableTasks` should be In Progress + Backlog only, recurring already excluded.
// `timesheets` should be unfiltered.
export function computeEngineerCapacity(
  engineers: string[],
  assignableTasks: Task[],
  timesheets: Timesheet[],
  currentWeekKey: string,
): EngineerCapacity[] {
  const remainingCapacity = remainingWorkdaysThisWeek() * HOURS_PER_WORKDAY
  return engineers.map(name => {
    const tasks = assignableTasks
      .filter(t => t.users.includes(name))
      .map(t => {
        const toggledThisWeek = timesheets
          .filter(ts => ts.task_id === t.id && ts.user === name && isoWeek(ts.date) === currentWeekKey)
          .reduce((a, ts) => a + ts.hours, 0)
        return {
          ...t,
          stageKind: t.stage === 'Backlog' ? ('backlog' as const) : ('inProgress' as const),
          leftInEstimation: t.alloc_h > 0 ? Math.max(t.alloc_h - t.eff_h, 0) : null,
          toggledThisWeek,
        }
      })

    const totalLeftInEstimation = tasks.reduce((a, t) => a + (t.leftInEstimation || 0), 0)
    const totalToggledThisWeek = tasks.reduce((a, t) => a + t.toggledThisWeek, 0)
    const freeHours = remainingCapacity - totalLeftInEstimation - totalToggledThisWeek

    return { name, tasks, totalLeftInEstimation, totalToggledThisWeek, remainingCapacity, freeHours }
  }).sort((a, b) => a.freeHours - b.freeHours)
}

// Shared by computeEngineerKPIs (per-engineer) and the Overview page's Insights
// section (team-wide), so the definition of "accurate" only lives in one place.
export function computeEstimationAccuracy(tasks: Task[]): number | null {
  const estTasks = tasks.filter(t => t.alloc_h > 0 && t.eff_h > 0)
  if (estTasks.length === 0) return null
  const accs = estTasks.map(t => Math.max(0, 100 - Math.abs(100 - (t.eff_h / t.alloc_h) * 100)))
  return accs.reduce((a, b) => a + b, 0) / accs.length
}

// Mirrors the "Previous Week Review" completion-rate logic on the Planning page —
// null (not 0%) when there was no plan at all, so an empty week doesn't read as a failure.
export function computePlanReliability(prevWeekItems: PlanItem[], tasks: Task[]): number | null {
  if (prevWeekItems.length === 0) return null
  const completed = prevWeekItems.filter(p => tasks.find(t => t.id === p.task_id)?.stage === 'Done' || p.status === 'completed')
  return (completed.length / prevWeekItems.length) * 100
}

export const SCORE_TIERS = [
  { key: 'failing', label: 'Failing', color: 'text-red-800 bg-red-50 dark:text-red-300 dark:bg-red-950/50' },
  { key: 'needs-imp', label: 'Needs Imp.', color: 'text-amber-800 bg-amber-50 dark:text-amber-300 dark:bg-amber-950/50' },
  { key: 'good', label: 'Good', color: 'text-emerald-800 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-950/50' },
  { key: 'very-good', label: 'Very Good', color: 'text-emerald-800 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-950/50' },
  { key: 'exemplary', label: 'Exemplary', color: 'text-emerald-800 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-950/50' },
]

export const KPI_DEFS: KPIDef[] = [
  { k: 'taskCompletion', label: 'Task Completion', unit: '%', thresholds: [80, 90, 95, 98], higherBetter: true },
  { k: 'slaAdherence', label: 'SLA Adherence', unit: '%', thresholds: [80, 90, 95, 100], higherBetter: true },
  { k: 'estAccuracy', label: 'Est. Accuracy', unit: '%', thresholds: [50, 70, 85, 95], higherBetter: true },
  { k: 'deadlineAdh', label: 'Deadline Adh.', unit: '%', thresholds: [60, 75, 85, 95], higherBetter: true },
  { k: 'firstResponse', label: '1st Response', unit: 'd', thresholds: [5, 3, 2, 1], higherBetter: false },
  { k: 'billableUtil', label: 'Billable Util.', unit: '%', thresholds: [40, 55, 65, 75], higherBetter: true },
  { k: 'meetingRatio', label: 'Meeting Ratio', unit: '%', thresholds: [50, 40, 30, 20], higherBetter: false },
  { k: 'backlogGrowth', label: 'Backlog Growth', unit: 'x', thresholds: [1.5, 1.2, 1.0, 0.8], higherBetter: false },
]

export function scoreTier(val: number | null, def: KPIDef): number {
  if (val === null || val === undefined || isNaN(val)) return -1
  const t = def.thresholds
  if (def.higherBetter) {
    if (val >= t[3]) return 4
    if (val >= t[2]) return 3
    if (val >= t[1]) return 2
    if (val >= t[0]) return 1
    return 0
  } else {
    if (val <= t[3]) return 4
    if (val <= t[2]) return 3
    if (val <= t[1]) return 2
    if (val <= t[0]) return 1
    return 0
  }
}

export function computeEngineerKPIs(
  eng: string,
  tasks: Task[],
  timesheets: Timesheet[],
  f: FilterParams,
  dateFrom?: string,
  dateTo?: string,
) {
  const useRange = dateFrom && dateTo
  const inP = useRange
    ? (ds: string) => inDateRange(ds, dateFrom!, dateTo!)
    : (ds: string) => inPeriod(ds, f)

  const engTasks = tasks.filter(t => {
    if (t.cancelled) return false
    if (!f.activeProjectIds.includes(t.projId)) return false
    if (f.category !== 'all' && t.cat !== f.category) return false
    return t.users.includes(eng)
  })

  const engTS = timesheets.filter(ts => {
    if (f.category !== 'all' && ts.cat !== f.category) return false
    return ts.user === eng
  })

  const periodTS = engTS.filter(ts => inP(ts.date))
  const doneTasks = engTasks.filter(t => t.stage === 'Done' && inP(t.last_stage))
  const activeTasks = engTasks.filter(t => t.stage !== 'Done' && t.stage !== 'Backlog' && t.stage !== 'Cancelled')

  const totalRelevant = doneTasks.length + activeTasks.length
  const taskCompletion = totalRelevant > 0 ? (doneTasks.length / totalRelevant) * 100 : null

  let slaOk = 0
  doneTasks.forEach(t => {
    const sla = SLA_THRESHOLDS[t.cat] || 30
    if (t.lead !== null && t.lead <= sla) slaOk++
  })
  const slaAdherence = doneTasks.length > 0 ? (slaOk / doneTasks.length) * 100 : null

  const estTasks = doneTasks.filter(t => t.alloc_h > 0 && t.eff_h > 0)
  const estAccuracy = computeEstimationAccuracy(doneTasks)

  const withDl = doneTasks.filter(t => t.deadline)
  let dlOk = 0
  withDl.forEach(t => { if (t.last_stage <= t.deadline) dlOk++ })
  const deadlineAdh = withDl.length > 0 ? (dlOk / withDl.length) * 100 : null

  const withAssign = engTasks.filter(t => t.created && t.assigned && inP(t.created))
  let firstResponse: number | null = null
  if (withAssign.length > 0) {
    const diffs = withAssign.map(t => Math.max(0, (new Date(t.assigned).getTime() - new Date(t.created).getTime()) / 864e5))
    firstResponse = diffs.reduce((a, b) => a + b, 0) / diffs.length
  }

  const totalH = periodTS.reduce((a, ts) => a + ts.hours, 0)
  const billH = periodTS.filter(ts => ts.cat === 'MSO' || ts.cat === 'PSO').reduce((a, ts) => a + ts.hours, 0)
  const billableUtil = totalH > 0 ? (billH / totalH) * 100 : null

  const meetH = periodTS.filter(ts => ts.is_meeting).reduce((a, ts) => a + ts.hours, 0)
  const meetingRatio = totalH > 0 ? (meetH / totalH) * 100 : null

  const createdInPeriod = engTasks.filter(t => inP(t.created)).length
  const backlogGrowth = doneTasks.length > 0 ? createdInPeriod / doneTasks.length : null

  return {
    taskCompletion, slaAdherence, estAccuracy, deadlineAdh,
    firstResponse, billableUtil, meetingRatio, backlogGrowth,
    _done: doneTasks.length, _active: activeTasks.length, _totalH: totalH,
    _estTaskCount: estTasks.length, _dlTaskCount: withDl.length,
    _doneTasks: doneTasks, _activeTasks: activeTasks, _estTasks: estTasks,
  }
}
